import React from 'react'
import { ScrollView } from 'react-native'

import { Stack, YStack } from '@zet/design-system/layout'
import { useBrandDetailsById } from '@zet/feature-brands/api/brands/hooks'
import { B18, R14 } from '@zet/integration-flow/components/typography'
import { i18n } from '@zet/localization'

import useVariantDetails from '../../hooks/useVariantDetails'

type JFSEligibilityCriteriaTabProps = {
  brandId: string | number
  variantId?: string | number
  variantData?: {
    salaryRangeIncomeCriteria?: {
      salaried?: {
        text: string
        value: number
      }
      selfEmployed?: {
        text: string
        value: number
      }
    }
  }
  onGotItPress?: () => void
}

export const JFSEligibilityCriteriaTab = ({
  brandId,
  variantId,
  variantData,
  onGotItPress,
}: JFSEligibilityCriteriaTabProps) => {
  const { data: content } = useBrandDetailsById(brandId || '')
  const { data: fetchedVariantData } = useVariantDetails({
    variantId: variantId,
  })

  // Use provided variantData or fetched variantData
  const finalVariantData = variantData || fetchedVariantData

  const salaried = content?.eligibilityCriteria?.salaried
  const selfEmployed = content?.eligibilityCriteria?.selfEmployed

  const salariedIncomeRange =
    finalVariantData?.salaryRangeIncomeCriteria?.salaried?.text ||
    content?.eligibilityCriteria?.salaried?.incomeRange ||
    ''

  const selfEmpIncomeRange =
    finalVariantData?.salaryRangeIncomeCriteria?.selfEmployed?.text ||
    content?.eligibilityCriteria?.selfEmployed?.incomeRange ||
    ''

  const renderCustomerSection = (
    type: 'salaried' | 'selfEmployed',
    ageGroup?: string,
    incomeRange?: string,
    documentsRequired?: Array<{
      heading: string
      content?: string
    }>,
  ) => {
    const customerTypeLabel =
      type === 'salaried' ? 'Salaried Customer' : 'Self-Employed Customer'

    return (
      <Stack className="mb-[16px] px-[16px]">
        {/* Customer Type Heading */}
        <B18 className="mb-[12px] text-grays-100">{customerTypeLabel}</B18>

        {/* Age Group */}
        {ageGroup && (
          <YStack className="mb-[12px]">
            <R14 className="mb-1 text-[#24262BA6]">{'Age Group'}</R14>
            <R14 className="text-[#24262BA6]">{ageGroup}</R14>
          </YStack>
        )}

        {/* Monthly Income Range */}
        {incomeRange && (
          <YStack className="mb-[12px]">
            <R14 className="mb-1 text-[#24262BA6]">
              {'Monthly Income Range'}
            </R14>
            <R14 className="text-[#24262BA6]">{incomeRange}</R14>
          </YStack>
        )}

        {/* Documents Required Section */}
        {documentsRequired &&
          documentsRequired.length > 0 &&
          documentsRequired.some((doc) => doc.content) && (
            <YStack className="mt-4">
              <B18 className="mb-2 text-black">{'Documents required'}</B18>

              {documentsRequired.map((doc, index) => {
                if (!doc.content) return null
                return (
                  <Stack key={index} className="mb-[12px]">
                    <R14 className="mb-1 text-[#24262BA6]">{doc.heading}</R14>
                    <R14 className="text-[#24262BA6] text-left">
                      {doc.content}
                    </R14>
                  </Stack>
                )
              })}
            </YStack>
          )}
      </Stack>
    )
  }

  const salariedDocuments = [
    {
      heading: i18n.t('eligibilityCriteria.identityProof'),
      content: salaried?.documentsRequired?.identityProof,
    },
    {
      heading: i18n.t('eligibilityCriteria.addressProof'),
      content: salaried?.documentsRequired?.addressProof,
    },
    {
      heading: i18n.t('eligibilityCriteria.incomeProof'),
      content: salaried?.documentsRequired?.incomeProof,
    },
    {
      heading: i18n.t('eligibilityCriteria.other'),
      content: (salaried?.documentsRequired as any)?.Other,
    },
  ]

  const selfEmployedDocuments = [
    {
      heading: i18n.t('eligibilityCriteria.identityProof'),
      content: selfEmployed?.documentsRequired?.identityProof,
    },
    {
      heading: i18n.t('eligibilityCriteria.addressProof'),
      content: selfEmployed?.documentsRequired?.addressProof,
    },
    {
      heading: i18n.t('eligibilityCriteria.incomeProof'),
      content: selfEmployed?.documentsRequired?.incomeProof,
    },
    {
      heading: i18n.t('eligibilityCriteria.other'),
      content: (selfEmployed?.documentsRequired as any)?.Other,
    },
  ]

  return (
    <YStack className="flex-1 bg-white" style={{ minHeight: '100%' }}>
      <ScrollView
        bounces={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 20 }}
        style={{ flex: 1 }}
      >
        {/* Salaried Customer Section */}
        {renderCustomerSection(
          'salaried',
          salaried?.ageGroup,
          salariedIncomeRange,
          salariedDocuments,
        )}

        <Stack className="h-[0.5px] mb-4 bg-[#191B1E1F] mx-[16px]" />

        {/* Self-Employed Customer Section */}
        {renderCustomerSection(
          'selfEmployed',
          selfEmployed?.ageGroup,
          selfEmpIncomeRange,
          selfEmployedDocuments,
        )}
      </ScrollView>
    </YStack>
  )
}
